import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import jwt_decode from "jwt-decode";

import {
  lastUserActivity,
  startUserActivityListener,
  stopUserActivityListener,
} from "./user-activity-listener";

// Function to log out the user
const logout = () => {
  stopUserActivityListener(); // Stop listening for user activity
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("token");
  localStorage.removeItem("permissions");
  localStorage.removeItem("user-app-info");
  localStorage.removeItem("appliedFilters-flex-scheduler");
  localStorage.removeItem("appliedFilters-monthly-activity-report");
  localStorage.removeItem("appliedFilters-daily-activity-report");
  localStorage.removeItem("appliedFilters-monthly-report");
  localStorage.removeItem("appliedFilters-daily-report");
  window.location.replace(`${window.location.origin}/login`);
};

let refreshTimeout: NodeJS.Timeout | null = null;

const scheduleTokenRefresh = () => {
  const refreshTime = 3 * 60 * 1000; // Schedule refresh 3 minutes after login

  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
  }

  refreshTimeout = setTimeout(() => {
    const timeSinceLastActivity = Date.now() - lastUserActivity;
    if (timeSinceLastActivity < 5 * 60 * 1000) {
      // User is active within the last 5 minutes
      refreshToken()
          .then(() => {
            /* Do nothing */
          })
          .catch((error) => {/* Do nothing */});
    }
  }, refreshTime);
};

// The server swaps the refresh token on every refresh and rejects the old one,
// so requests that hit 401 together (a page loading its data in parallel) must
// share one refresh: a second refresh with the old token would sign the user out.
let refreshInFlight: Promise<string> | null = null;

const refreshOnce = (): Promise<string> => {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${process.env.REACT_APP_API_END_POINT}/Users/Refresh`, {
        refreshToken: localStorage.getItem("refreshToken"),
        token: localStorage.getItem("token"),
        clientType: "web",
      })
      .then((res) => {
        if (res.status !== 200) throw new Error("Failed to refresh token");
        localStorage.setItem("refreshToken", res.data.refreshToken);
        localStorage.setItem("token", res.data.token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
        return res.data.token as string;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

const refreshToken = async () => {
  try {
    const token = await refreshOnce();

    // Schedule the next token refresh
    scheduleTokenRefresh();

    return token;
  } catch (error) {
    logout();
    return Promise.reject(error);
  }
};

// Endpoints that sign a supervisor in and return the /Users/authenticate body.
const LOGIN_PATHS = [
  "/authenticate",
  "/authenticateSSO",
  "/VerifyLoginCode",
  "/HandoffLogin",
];

// Anonymous code-based sign-in endpoints. A 401 here means a wrong or expired
// code, not an expired session, so it must reach the caller instead of
// triggering the refresh-or-logout flow below.
const CODE_LOGIN_PATHS = ["/SendLoginCode", "/VerifyLoginCode", "/HandoffLogin"];

const urlEndsWithAny = (url: string | undefined, paths: string[]) =>
  paths.some((path) => url?.endsWith(path) ?? false);

// Request interceptor to add authorization headers
axios.interceptors.request.use((config: AxiosRequestConfig) => {
  if (localStorage.token) {
    config.headers.Authorization = `Bearer ${localStorage.token}`;
  }
  return config;
});

// Response interceptor to handle token refresh logic
axios.interceptors.response.use(
  (response: AxiosResponse) => {
    // Intercept successful login response
    if (urlEndsWithAny(response.config.url, LOGIN_PATHS)) {
      scheduleTokenRefresh();
      startUserActivityListener(); // Start listening for user activity
    }
    return response;
  },
  function (error) {
    const originalRequest = error.config;

    if (urlEndsWithAny(originalRequest?.url, CODE_LOGIN_PATHS)) {
      return Promise.reject(error);
    }

      // If the refresh token request itself failed, log the user out
      if (
          error.response.status === 401 &&
          originalRequest.url ===
          `${process.env.REACT_APP_API_END_POINT}/Users/Refresh`
      ) {
        logout();
        return Promise.reject(error);
      }

      // If the original request failed due to 401, try to refresh the token
      if (error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        return refreshOnce()
            .then(() => axios(originalRequest))
            .catch((error) => {
              try {
                if (
                    localStorage.getItem("refreshToken") &&
                    localStorage.getItem("token")
                ) {
                  const token = localStorage.getItem("token");
                  const jwt: { exp: number } = jwt_decode(token ?? "") ?? {
                    exp: 0,
                  };
                  const exp = jwt ? jwt.exp ?? 0 : 0;

                  if (Date.now() / 1000 < exp) {
                    axios.defaults.headers.common["Authorization"] =
                        "Bearer " + token;
                    return axios(originalRequest);
                  } else {
                    logout();
                    return Promise.reject(error);
                  }
                } else {
                  logout();
                  return Promise.reject(error);
                }
              } catch (e) {
                logout();
                return Promise.reject(error);
              }
            });
      }

      return Promise.reject(error);
    }
);

export default axios;